import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import {
  Phone, ArrowRight, MapPin, Clock, Star, ShieldAlert, Youtube,
  Camera, Plane, Car, CheckCircle2, Quote,
} from "lucide-react";
import { Reveal, SectionHeading, Counter, SmartImage } from "../components/common";
import EnquiryForm from "../components/EnquiryForm";
import {
  business, stats, services, scamTips, avoidPrograms, testimonials, hours, gallery,
} from "../data/mock";

const pillars = [
  { to: "/photography", title: "Photography", desc: "Canvas prints of the great outdoors, shot locally.", icon: Camera, color: "from-sky-500 to-cyan-500" },
  { to: "/drone", title: "Drone Work", desc: "Licensed commercial aerial photo & video.", icon: Plane, color: "from-violet-500 to-indigo-500" },
  { to: "/fpv", title: "FPV Jack Point Covers", desc: "Hand-built Ford Performance Vehicle parts.", icon: Car, color: "from-orange-500 to-amber-500" },
];

export default function Home() {
  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-28">
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-sky-50 via-white to-white" />
        <motion.div
          className="absolute z-0 top-20 -left-20 w-96 h-96 rounded-full bg-sky-300/30 blur-3xl"
          animate={{ y: [0, 30, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute z-0 top-40 -right-16 w-80 h-80 rounded-full bg-orange-300/30 blur-3xl"
          animate={{ y: [0, -30, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 shadow-sm px-4 py-1.5 text-sm font-semibold text-slate-700"
            >
              <span className="flex h-2 w-2"><span className="animate-ping absolute h-2 w-2 rounded-full bg-green-400 opacity-75" /><span className="h-2 w-2 rounded-full bg-green-500" /></span>
              Local &amp; trusted since {business.since}
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-slate-900"
            >
              Tech trouble?{" "}
              <span className="bg-gradient-to-r from-sky-500 to-orange-500 bg-clip-text text-transparent">Call Fred.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
              className="mt-5 text-lg text-slate-600 max-w-xl"
            >
              Friendly, honest, in-home computer, phone and security help across Cobram, Barooga and the surrounding area. No jargon. No pressure. Just a fix that lasts.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <a href={business.phoneHref} className="inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3.5 shadow-lg shadow-orange-500/30 transition-all hover:-translate-y-0.5">
                <Phone size={18} /> Call {business.phone}
              </a>
              <a href="#services" className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 text-slate-800 font-semibold px-6 py-3.5 shadow-sm hover:border-sky-300 hover:text-sky-600 transition-all">
                See services <ArrowRight size={16} />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500"
            >
              {["On-site or drop-off", "Fair local pricing", "Same-week service", "Plain-English advice"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle2 size={16} className="text-sky-500" /> {t}</span>
              ))}
            </motion.div>
          </div>

          {/* Pillars card stack */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="grid gap-4"
          >
            {pillars.map((p, i) => (
              <motion.div key={p.to} whileHover={{ y: -4 }} transition={{ type: "spring", stiffness: 300 }}>
                <Link to={p.to} className="group flex items-center gap-4 rounded-2xl bg-white border border-slate-200 shadow-lg shadow-slate-200/50 p-5 hover:shadow-xl transition-shadow">
                  <span className={`flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${p.color} text-white shadow-md`}>
                    <p.icon size={26} />
                  </span>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-slate-900 group-hover:text-sky-600 transition-colors">{p.title}</h3>
                    <p className="text-sm text-slate-500">{p.desc}</p>
                  </div>
                  <ArrowRight size={20} className="text-slate-300 group-hover:text-sky-500 group-hover:translate-x-1 transition-all" />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-14 bg-slate-900">
        <div className="max-w-7xl mx-auto px-5 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="text-center">
              <div className="font-heading text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-sky-400 to-orange-400 bg-clip-text text-transparent">
                <Counter value={s.value} suffix={s.suffix} decimal={s.decimal} />
              </div>
              <p className="mt-2 text-sm text-slate-400">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20 md:py-28 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5">
          <SectionHeading eyebrow="What I Do" title="All your tech, sorted — by one friendly local." subtitle="From a slow laptop to a full home security setup, here's how I can help." />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((s, i) => {
              const Icon = Icons[s.icon] || Icons.Wrench;
              return (
                <Reveal key={s.title} delay={(i % 4) * 0.06}>
                  <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 300 }}
                    className="group h-full rounded-2xl bg-white border border-slate-200 p-6 hover:border-sky-200 hover:shadow-xl hover:shadow-sky-100/50 transition-all">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-sky-50 text-sky-600 group-hover:bg-sky-500 group-hover:text-white transition-colors"><Icon size={24} /></span>
                      <span className="font-heading text-2xl font-bold text-slate-100 group-hover:text-sky-100 transition-colors">{s.n}</span>
                    </div>
                    <h3 className="mt-5 font-heading font-bold text-lg text-slate-900">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-10 text-center">
            <p className="text-slate-600">Not sure if I can help? <span className="text-slate-900 font-medium">Give me a quick call — if I can't fix it, I'll point you to someone who can.</span></p>
            <Link to="/contact" className="mt-4 inline-flex items-center gap-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-semibold px-6 py-3 shadow-lg shadow-sky-500/30 transition-all hover:-translate-y-0.5">Ask Fred <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="inline-block text-sm font-semibold tracking-wider uppercase text-sky-600 mb-3">Meet Fred</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900">Local. Honest. Been doing this since 2006.</h2>
            <div className="mt-5 space-y-4 text-slate-600 leading-relaxed">
              <p>Fred's Home Technical Services has been running full-time since 2006 — and long before that Fred was quietly fixing computers for friends and family after hours. He's lived in Cobram all his life.</p>
              <p>He works with home users and small businesses within about an hour of Cobram / Barooga on computers, phones, tablets, security systems, DVRs and security cameras. If it has a power cable, there's a good chance Fred can help.</p>
              <p>Outside of work he's a keen photographer and licensed drone pilot — and he builds FPV Jack Point Covers for Ford Performance Vehicles too.</p>
            </div>
            <a href={business.youtube} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 font-semibold text-slate-800 hover:border-red-200 hover:text-red-600 transition-all">
              <Youtube size={18} className="text-red-500" /> Watch on YouTube — MrFredMustica
            </a>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Photography", "Drone Video", "Video Production", "Electronics Projects", "Travel", "Projects with the Kids"].map((t) => (
                <span key={t} className="rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600">{t}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 gap-4">
              <SmartImage src={gallery[3].src} fallback={gallery[3].fb} alt="Fred's photography" className="rounded-2xl h-56 w-full object-cover shadow-lg" />
              <SmartImage src={gallery[0].src} fallback={gallery[0].fb} alt="Aerial" className="rounded-2xl h-56 w-full object-cover shadow-lg mt-8" />
              <SmartImage src={gallery[6].src} fallback={gallery[6].fb} alt="Landscape" className="rounded-2xl h-56 w-full object-cover shadow-lg" />
              <SmartImage src={gallery[1].src} fallback={gallery[1].fb} alt="Water" className="rounded-2xl h-56 w-full object-cover shadow-lg mt-8" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SCAM SAFETY */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5">
          <div className="flex items-center gap-2 justify-center text-red-600 mb-3"><ShieldAlert size={20} /><span className="text-sm font-semibold tracking-wider uppercase">Stay Safe Online</span></div>
          <SectionHeading title="Don't get scammed. Read this first." subtitle="Scammers target locals every day. Here's what you need to know to protect yourself and your family." />
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {scamTips.map((t, i) => (
              <Reveal key={t.title} delay={(i % 2) * 0.08}>
                <div className="h-full rounded-2xl border border-red-100 bg-red-50/50 p-6">
                  <h3 className="font-heading font-bold text-slate-900 flex items-start gap-2"><ShieldAlert size={20} className="text-red-500 mt-0.5 shrink-0" /> {t.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed pl-7">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10">
            <div className="rounded-2xl bg-slate-900 p-8 text-center">
              <h4 className="font-heading font-bold text-white text-lg">Programs to avoid — do not install these</h4>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {avoidPrograms.map((p) => (<span key={p} className="rounded-full bg-slate-800 text-slate-300 px-3 py-1 text-xs">{p}</span>))}
              </div>
              <p className="mt-6 text-slate-400 text-sm">Got a suspicious pop-up right now? Try holding <span className="text-white font-semibold">ALT + F4</span> a few times. If in doubt, call Fred before you click anything.</p>
              <a href="https://www.scamwatch.gov.au/" target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 font-semibold px-5 py-2.5 hover:bg-slate-100 transition-colors">Visit Scamwatch · Australia <ArrowRight size={16} /></a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5">
          <SectionHeading eyebrow="What Locals Say" title="Trusted by neighbours across Cobram, Barooga & beyond." />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={(i % 4) * 0.06}>
                <div className="h-full rounded-2xl bg-white border border-slate-200 p-6 shadow-sm hover:shadow-lg transition-shadow">
                  <Quote size={28} className="text-sky-200" />
                  <div className="mt-2 flex gap-0.5 text-amber-400">{[...Array(5)].map((_, k) => <Star key={k} size={15} fill="currentColor" />)}</div>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">“{t.quote}”</p>
                  <div className="mt-4 font-semibold text-slate-900 text-sm">{t.name}<span className="block text-xs font-normal text-slate-500">{t.town}</span></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section id="contact" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionHeading center={false} eyebrow="Get in Touch" title="Ready when you are." subtitle="Call the mobile, drop an email, or leave a quick message. Fred replies personally — usually the same day." />
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <a href={business.phoneHref} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-sky-300 hover:shadow-lg transition-all">
                <Phone className="text-sky-500" /><div className="mt-3 font-heading font-bold text-slate-900">Call mobile</div><div className="text-slate-600 text-sm">{business.phone}</div>
              </a>
              <a href={business.emailHref} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-sky-300 hover:shadow-lg transition-all">
                <Icons.Mail className="text-sky-500" /><div className="mt-3 font-heading font-bold text-slate-900">Email</div><div className="text-slate-600 text-sm">{business.email}</div>
              </a>
            </div>
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 font-heading font-bold text-slate-900"><Clock size={18} className="text-sky-500" /> Opening Hours</div>
              <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                {hours.map((h) => (<li key={h.day} className="flex justify-between"><span className="text-slate-500">{h.day}</span><span className={h.open ? "text-slate-800 font-medium" : "text-slate-400"}>{h.time}</span></li>))}
              </ul>
              <div className="mt-4 flex items-start gap-2 text-sm text-slate-500"><MapPin size={16} className="text-sky-500 mt-0.5" /> {business.area}</div>
              <p className="mt-3 text-xs text-orange-600 bg-orange-50 rounded-lg px-3 py-2">Please call the mobile only — landline is unplugged. Leave a message if I'm busy.</p>
            </div>
          </div>
          <Reveal delay={0.1}>
            <EnquiryForm type="general" title="Send Fred a quick message" subtitle="He'll get back to you as soon as he's off the bench." />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
